import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { 
  Loader2, LayoutDashboard, BookOpen, Package, Users, 
  Mail, Share2, Settings, ArrowRight, Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSidebar = () => {
  const location = useLocation();
  
  const links = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/admin/books', icon: BookOpen, label: 'Books' },
    { to: '/admin/orders', icon: Package, label: 'Orders' },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
    { to: '/admin/social', icon: Share2, label: 'Social Media' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <aside className="w-64 bg-gradient-to-b from-pink-50/80 to-white border-r border-pink-100/60 min-h-screen p-4" data-testid="admin-sidebar">
      <div className="mb-8 px-3 pt-2">
        <Link to="/" className="flex items-center gap-2" data-testid="admin-logo">
          <Heart className="h-5 w-5 text-primary" strokeWidth={1.5} fill="currentColor" />
          <span className="font-script text-2xl" style={{color: 'hsl(340, 30%, 25%)'}}>Dior Russell</span>
        </Link>
        <p className="text-[10px] font-accent uppercase tracking-[0.2em] text-muted-foreground mt-1 pl-7">Admin</p>
      </div>
      <nav className="space-y-1" data-testid="admin-nav">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            data-testid={`admin-nav-${link.label.toLowerCase().replace(' ', '-')}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              isActive(link.to, link.exact)
                ? 'bg-primary text-primary-foreground shadow-soft'
                : 'text-muted-foreground hover:bg-pink-100/50 hover:text-foreground'
            }`}
          >
            <link.icon className="h-4 w-4" strokeWidth={1.5} />
            {link.label}
          </Link>
        ))}
      </nav>
    </aside>
  );
};

export const AdminLayout = () => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md border-pink-100/60 text-center rounded-2xl">
          <CardContent className="pt-8 pb-6">
            <h2 className="font-heading text-2xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-6">
              You need admin privileges to access this page.
            </p>
            <Button asChild className="rounded-full">
              <Link to="/">Go Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      <main className="flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
};

export const AdminDashboardPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/admin/stats`, {
          headers,
          withCredentials: true
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
      setLoading(false);
    };

    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl font-bold mb-8" style={{color: 'hsl(340, 30%, 25%)'}}>Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Books', value: stats?.total_books || 0, icon: BookOpen },
          { label: 'Total Orders', value: stats?.total_orders || 0, icon: Package },
          { label: 'Total Users', value: stats?.total_users || 0, icon: Users },
          { label: 'Total Revenue', value: `$${stats?.total_revenue?.toFixed(2) || '0.00'}`, icon: Heart, highlight: true },
        ].map((stat, i) => (
          <Card key={i} className="border-pink-100/60 rounded-xl overflow-hidden">
            <CardContent className="pt-6 flex items-start justify-between">
              <div>
                <p className={`text-2xl font-bold ${stat.highlight ? 'text-primary' : ''}`}>{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
              <div className="p-2.5 rounded-full bg-primary/8">
                <stat.icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="border-pink-100/60 rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-xl" style={{color: 'hsl(340, 30%, 25%)'}}>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { to: '/admin/books', title: 'Manage Books', desc: 'Add, edit or remove books' },
              { to: '/admin/newsletter', title: 'Send Newsletter', desc: 'Email your subscribers' },
              { to: '/admin/social', title: 'Social Media', desc: 'Post to social platforms' },
            ].map((action) => (
              <Link key={action.to} to={action.to} className="group">
                <div className="p-4 border border-pink-100/60 rounded-xl hover:bg-pink-50/50 hover:border-primary/20 transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{action.title}</p>
                      <p className="text-sm text-muted-foreground">{action.desc}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
